import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { Route, Router, applyRouterMiddleware, browserHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import queryString from 'query-string';
import axios from 'axios';
import _ from 'lodash';
import { ToastContainer } from 'react-toastify';
import { useScroll } from 'react-router-scroll';
import 'semantic-ui-css/semantic.min.css';
import 'react-toastify/dist/ReactToastify.min.css';

import { authService } from './utils/auth-service';
import App from './app';
import Sandbox from './containers/sandbox';
import TestContainer from './containers/test-container';
import Homepage from './containers/homepage';
import Dashboard from './containers/dashboard';
import ReportsList from './containers/report-list';
import Campaign from './containers/campaign';
import TagsManager from './containers/tags-manager';
import TagCloud from './containers/tag-cloud';
import AcrCampaign from './containers/acr-campaign';
import PendingInvitations from './containers/pending-invitations';
import CampaignNew from './containers/campaign-new';
import ViewAssetPage from './containers/view-asset-page';
import CampaignUpdate from './containers/campaign-update';
import CampaignMembers from './containers/campaign-members';
import CampaignMembersManage from './containers/campaign-members-manage';
import DiscussionCreate from './containers/discussion-create';
import Collaboration from './containers/collaboration';
import UserProfile from './containers/user-profile';
import UserSettings from './containers/user-settings';
import MediaKit from './containers/media-kit';
import Login from './containers/login';
import Signup from './containers/signup';
import NONACRCampaignSetup from './containers/non-acr-campaign-setup';
import CampaignInvite from './containers/campaign-invite';
import CampaignManage from './containers/campaign-manage';
import OrganisationAuditLog from './containers/organisation-audit-log';
import CampaignInviteMore from './containers/campaign-invite-more';
import ResetPassword from './containers/reset-password';
import NotFoundPage from './components/not-found-page';
import User from './containers/user';
import Analytics from './containers/analytics';
import AnalyticsAsset from './containers/analytics-asset';
import DiscussionsList from './containers/discussion-list2';
import CampaignAuditTrail from './containers/campaign-audit-trail';
import CampaignDashboard from './containers/campaign-dashboard';
import Deliverable from './containers/deliverable';
import Onboarding from './components/onboarding';
import Payments from './containers/payments';
import AccessDenied from './components/access-denied';
import CompanySignup from './containers/company-signup';
import MyInfluencers from './containers/my-influencers';
import MyCreators from './containers/my-creators';
import MyCreators2 from './containers/my-creators2';
import TeamManager from './containers/team-manager';
import HomePage from './containers/home-page';
import Creator from './containers/creator';
import CreatorNew from './containers/creator-new';
import CreatorSignUp from './containers/creator-full';
import CampaignNotificationsTab2 from './components/campaign-notifications-tab2';

import CampaignSettings from './containers/campaign-settings';

import Shortlists from './containers/shortlists';
import Shortlist from './containers/shortlist';

import CampaignsV2 from './containers/campaigns-v2';

import CampaignDashboardV2 from './containers/campaign-dashboard-v2';
import CampaignReport from './containers/campaign-dashboard-report';
import CampaignDashboardAssets from './containers/campaign-dashboard-assets';
import CampaignDashboardTeam from './containers/campaign-dashboard-team';
import CampaignDashboardOverview from './containers/campaign-dashboard-overview';
import CampaignDashboardSettings from './containers/campaign-dashboard-settings';
import CampaignDashboardInfluencers from './containers/campaign-dashboard-influencers';

import InfluencerAuthorisation from './containers/influencer-authorisation';
import InfluencerPage from './containers/influencer-page';
import InfluencerLatest from './components/influencer-latest';
import InfluencerInstagramStories from './containers/influencer-instagram-stories';
import InfluencerAdPosts from './components/influencer-ad-posts';
import InfluencerProfileStats from './components/influencer-profile-stats';
import InfluencerReviews from './components/influencer-reviews';
import InfluencerCampaigns from './components/influencer-campaigns';
import InfluencerBlogosphere from './components/influencer-blogosphere';
import InfluencerPostRates from './containers/influencer-post-rates';
import InfluencerQuestionnaire from './containers/influencer-questionnaire';
import InfluencerProfile from './containers/influencer-profile';
import AgencyInfluencerRequests from './containers/agency-influencer-requests';
import InfluencerForm from './components/influencer-form';
import InfluencerRequests from './containers/influencer-requests';
import CreatorPage from './containers/creator-page';
import ShopfluenceDashboard from './containers/shopfluence-dashboard';
import SkinsidersDashboard from './containers/skinsiders-dashboard';
import AbbottLyonDashboard from './containers/abbott-lyon-dashboard';
import OrdersDashboard from './containers/orders-dashboard';
import ElevenMeadowDashboard from './containers/eleven-meadow-dashboard';

import NewInfluencer from './containers/new-influencer';
import NewInfluencerSignup from './containers/new-influencer-signup';
import NewInfluencerRegistration from './containers/new-influencer-registration';
import NewInfluencerWelcome from './containers/new-influencer-welcome';
import NewInfluencerProfile from './containers/new-influencer-profile';
import TokenGenerationPage from './containers/token-generation-page';
import TokenThanks from './components/token-thanks';
import ApplyThanks from './components/apply-thanks';
import RegisterThanks from './components/register-thanks';

import Profiles from './containers/profiles';

import MembersTab from './containers/team-manager/members-tab';
import SettingsTab from './containers/team-manager/settings-tab';
import TagsTab from './containers/team-manager/tags-tab';
import InventoryTab from './containers/team-manager/inventory-tab';
import BusinessUnitsTab from './containers/team-manager/business-units-tab';

import EntrySurvey from './containers/entry-survey';
import ThemeSelector from './themes/theme-selector';

import EmbeddedChat from './containers/chat';
import Mailouts from './containers/mailouts';
import UnsubscribeConfirmation from './containers/unsubscribe-confirmation';
import LaunchMetricsDashboard from './containers/launch-metrics-dashboard';

import sagas from './sagas';
import reducer from './reducer';
import './index.scss';

import './the-influencer.css';

const theme = ThemeSelector.theme();
const pageTitle = theme.title || 'dyzio';

const HIDE_HOMEPAGE = theme.hideHomepage || false;

// Configure segment analytics
analytics.load(process.env.REACT_APP_SEGMENT_API_KEY);
analytics.page();

// onEnter callback to validate authentication in private routes
const requireAuth = (nextState, replace) => {
  if (!authService.loggedIn()) {
    localStorage.setItem('path', nextState.location.pathname);
    replace({ pathname: '/' });
  }
};

// onEnter callback to validate authentication in non-private routes
const notRequireAuth = (nextState, replace) => {
  const { hash } = window.location;
  const parsedHash = queryString.parse(hash);

  const FORCE_RELOAD = true;
  // LAB - the reload is being forced because the auth token is not available
  // in the constructor of the data HOCs unless we write to storage and
  // then reload the app. I'm sure this could be fixed using React Context
  // or some kind of provider pattern.
  let pathname;

  if (authService.loggedIn()) {
    const profile = authService.getProfile();
    const { role, enrolledBy } = profile.app_metadata.dyzio;
    if (role && role === 'INFLUENCER') {
      if (enrolledBy === 'SELF') {
        pathname = '/new-influencer/welcome';
      } else {
        pathname = '/dashboard';
      }
    } else if (parsedHash.campaign) {
      pathname = `/campaign/${parsedHash.campaign}`;
    } else {
      const destination = HIDE_HOMEPAGE ? '/dashboard' : '/home';
      pathname = destination;
    }
    if (FORCE_RELOAD) window.location = pathname;
    replace({ pathname });
  }
};

const logoutUser = (nextState, replace) => {
  const redirectUri = _.get(nextState, 'location.query.redirect_uri');
  authService.logout();
  if (redirectUri) {
    // eslint-disable-next-line lodash/prefer-lodash-method
    window.location.replace(redirectUri);
  } else {
    replace({ pathname: '/' });
  }
};

const parseAuthHash = (nextState, replace, callback) => {
  const { hash } = window.location;
  const parsedHash = queryString.parse(hash);

  return authService.parseHash().then(() => {
    if (parsedHash.campaign) {
      replace({ pathname: `/campaign/${parsedHash.campaign}` });
    } else {
      replace({ pathname: '/invite' });
    }
    return callback();
  }).catch(() => {
    replace({ pathname: `/access-denied/${hash}` });
    return callback();
  });
};


const parseInfluencerAuthHash = (nextState, replace, callback) =>
  authService.parseHash().then(() => {
    replace({ pathname: '/new-influencer/registration' });
    callback();
  }).catch(() => {
    replace({ pathname: '/access-denied' });
    return callback();
  });


const processSurveyComplete = async (nextState, replace, callback) => {
  console.log('processSurveyComplete');

  const profile = authService.getProfile();
  const { o_id: organisationId } = profile.app_metadata.dyzio;
  const token = authService.getToken();
  const ax = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 500,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = {
    data: {
      attributes: {
        entrySurveyStatus: 'COMPLETED',
      },
    },
  };

  await ax.patch(`/organisations/${organisationId}`, payload);

  replace({ pathname: '/home' });
  return callback();
};

// create the saga middleware
const sagaMiddleware = createSagaMiddleware();
// mount it on the Store
const store = createStore(reducer, applyMiddleware(sagaMiddleware));

// Create an enhanced history that syncs navigation events with the store
const history = syncHistoryWithStore(browserHistory, store);

sagaMiddleware.run(sagas);

ReactDOM.render(
  <div>
    <Helmet>
      <meta charSet="utf-8" />
      <title>{pageTitle}</title>
    </Helmet>

    <Provider store={store}>
      <Router
        history={history}
        // Scroll to top when going to a new page, imitating default browser behaviour
        render={applyRouterMiddleware(useScroll())}
      >
        {/* External pages start */}
        <Route component={TokenThanks} exact path="token-thanks" />
        <Route component={RegisterThanks} exact path="register" />
        <Route component={CreatorNew} path="creator/new" />
        <Route component={CreatorSignUp} path="creator/signup" />
        <Route component={CreatorSignUp} path="brand/:brand/signup" />
        <Route component={UnsubscribeConfirmation} exact path="creator/unsubscribe" />
        <Route component={ApplyThanks} exact path="creator/thanks" />
        <Route component={Creator} path="creator/:id" />

        {/* External pages end */}
        <Route component={App}>
          <Route path="/" component={Homepage} onEnter={notRequireAuth} />
          <Route path="media-kit/:influnecerId" component={MediaKit} onEnter={requireAuth} />


          <Route mobileAllowed path="new-influencer/signup" component={NewInfluencerSignup} />
          <Route mobileAllowed path="new-influencer" component={NewInfluencer} onEnter={requireAuth}>
            <Route path="registration" component={NewInfluencerRegistration} />
            <Route path="welcome" component={NewInfluencerWelcome} />
            <Route path="profile" component={NewInfluencerProfile} />
          </Route>

          <Route component={User} onEnter={requireAuth}>
            <Route path="dashboard" component={Dashboard} />
            <Route path="launch-metrics/dashboard" component={LaunchMetricsDashboard} />
            <Route path="mailouts/:id" component={Mailouts} />
            <Route path="survey" component={EntrySurvey} />
            <Route path="survey-complete" onEnter={processSurveyComplete} />
            <Route path="shopfluence/dashboard" component={ShopfluenceDashboard} />
            <Route path="skinsiders/dashboard" component={SkinsidersDashboard} />
            <Route path="abbott-lyon/dashboard" component={AbbottLyonDashboard} />
            <Route path="abbott-lyon/orders" component={OrdersDashboard} />
            <Route path="eleven-meadow/dashboard" component={ElevenMeadowDashboard} />
            <Route path="home" component={HomePage} />
            <Route path="profile" component={UserProfile} />
            <Route path="profile/settings" component={UserSettings} />
            <Route path="profiles" component={Profiles} />
            <Route path="campaigns/v2" component={CampaignsV2} />
            <Route path="organisations/events" component={Sandbox} />
            <Route path="pending-invitations" component={PendingInvitations} />
            <Route path="campaign/v2/:id" component={CampaignDashboardV2}>
              <Route path="assets" component={CampaignDashboardAssets} />
              <Route path="overview" component={CampaignDashboardOverview} />
              <Route path="report" component={CampaignReport} />
              <Route path="team" component={CampaignDashboardTeam} />
              <Route path="settings" component={CampaignDashboardSettings} />
              <Route path="influencers" component={CampaignDashboardInfluencers} />
            </Route>

            <Route path="acr-campaign">
              <Route path="members" component={CampaignMembers} />
              <Route path="members/manage" component={CampaignMembersManage} />
              <Route path="update/:id" component={CampaignUpdate} />
              <Route path="discussioncreate" component={DiscussionCreate} />
              <Route path="deliverable/:id" component={Deliverable} />
              <Route path=":id" component={AcrCampaign} />
              <Route path=":id/manage" component={CampaignManage} />
              <Route path=":id/invite" component={CampaignInviteMore} />
            </Route>

            <Route path="asset/:assetType/:assetId" component={ViewAssetPage} />

            <Route
              path="campaign" component={Campaign}
            >

              <Route
                path=":id"
                component={
                  props => props.history.push(`/campaign/${props.params.id}/collaborations`)
                }
              />

              <Route
                path=":id/setup"
                componend={NONACRCampaignSetup}
              />

              <Route
                path=":id/collaboration/:collabId"
                component={Collaboration}
              />
              <Route
                path=":id/collaborations"
                component={DiscussionsList}
              />

              <Route
                path=":id/audit-trail"
                component={CampaignAuditTrail}
              />
              <Route
                path=":id/analytics"
                component={CampaignDashboard}
              />
              <Route
                path=":id/reports"
                component={ReportsList}
              />

              <Route
                path=":id/payments"
                component={Payments}
              />

              <Route
                path=":id/notifications"
                component={CampaignNotificationsTab2}
              />

              <Route
                path=":id/settings"
                component={CampaignSettings}
              />
            </Route>

            <Route path="campaign/new" component={CampaignNew} />
            <Route path="campaign/members" component={CampaignMembers} />
            <Route path="campaign/members/manage" component={CampaignMembersManage} />
            <Route path="campaign/update/:id" component={CampaignUpdate} />
            <Route path="campaign/discussioncreate" component={DiscussionCreate} />
            <Route path="campaign/deliverable/:id" component={Deliverable} />
            <Route path="campaign/:id/manage" component={CampaignManage} />
            <Route path="campaign/:id/invite" component={CampaignInviteMore} />
            <Route path="team" component={TeamManager}>
              <Route path="members" component={MembersTab} />
              <Route path="manage" component={SettingsTab} />
              <Route path="tags" component={TagsTab} />
              {<Route path="inventory" component={InventoryTab} />}
              <Route path="business-units" component={BusinessUnitsTab} />
            </Route>
            <Route path="organisation">
              <Route path="audit-log" component={OrganisationAuditLog} />
            </Route>
            <Route path="influencer">
              <Route path="authorisation" component={InfluencerAuthorisation} />
              <Route path="my-requests" component={InfluencerRequests} />
            </Route>

            <Route path="chat" component={EmbeddedChat} />
            <Route path="sandbox" component={() => <div style={{ width: 800, margin: '20px auto' }}><TagCloud onTagSelected={id => console.log(id)} /></div>} />
            <Route path="shortlists" component={Shortlists} />
            <Route path="shortlists/:id" component={Shortlist} />
            <Route path="analytics" component={Analytics} />
            <Route path="analytics/:id" component={AnalyticsAsset} />
            <Route path="influencers" component={MyInfluencers} />
            <Route path="creators" component={MyCreators} />
            <Route path="creators2" component={MyCreators2} />
            <Route exact path="creators/approvals" component={MyCreators} />
            <Route path="creators/:id" component={CreatorPage} />

            <Route path="my-requests" component={AgencyInfluencerRequests} />
            <Route exact path="influencer/add" component={InfluencerForm} />
            <Route path="influencer" component={InfluencerPage}>
              <Route path=":id/latest" component={InfluencerLatest} />
              <Route path=":id/ads" component={InfluencerAdPosts} />
              <Route path=":id/statistics" component={InfluencerProfileStats} />
              <Route path=":id/reviews" component={InfluencerReviews} />
              <Route path=":id/campaigns" component={InfluencerCampaigns} />
              <Route path=":id/profile" component={InfluencerProfile} />
              <Route path=":id/stories" component={InfluencerInstagramStories} />
              <Route path=":id/blogosphere" component={InfluencerBlogosphere} />
              <Route path=":id/rates" component={InfluencerPostRates} />
              <Route path=":id/questionnaire" component={InfluencerQuestionnaire} />
            </Route>

            <Route path="onboarding" component={Onboarding} />

            <Route path="/token/generate/:tokenId" component={TokenGenerationPage} />

          </Route>
          <Route onEnter={notRequireAuth}>
            <Route path="login" component={Login} />
            <Route path="signup" component={Signup} />
            <Route path="company/signup" component={CompanySignup} />
            <Route
              path="auth"
              onEnter={(nextState, replace, callback) => {
                parseAuthHash(nextState, replace, callback);
              }}
            />
            <Route
              path="influencer-auth"
              onEnter={(nextState, replace, callback) => {
                parseInfluencerAuthHash(nextState, replace, callback);
              }}
            />
          </Route>
          <Route path="invite/:inviteid" component={CampaignInvite} />
          <Route path="invite" component={CampaignInvite} />
          <Route path="resetpassword" component={ResetPassword} />
          <Route path="logout" onEnter={logoutUser} />
          <Route path="access-denied/*" component={AccessDenied} />
          <Route path="not-found" component={NotFoundPage} />
          <Route path="*" component={NotFoundPage} />
        </Route>
      </Router>
    </Provider>
    <ToastContainer />
  </div>,
  document.getElementById('root'),
);



/** WEBPACK FOOTER **
 ** ./src/index.js
 **/